// selectors
import { get, camelCase } from 'lodash';
import { createSelector } from 'reselect';
import * as storeConstants from 'store/constants';
import createCachedSelector from 're-reselect';

export const pickAlerts = state => state.alerts || {};
export const pickAlertsArray = state => Object.values(state.alerts || {});

export const getAlerts = createSelector(
  [pickAlertsArray],
  alerts => alerts
);

export const getAlert = (fingerprint) => createSelector(
  [pickAlerts],
  alerts => alerts[fingerprint]
);

export const getSystemManagedAlerts = createSelector(
  [pickAlertsArray],
  alerts => alerts.filter(alert => alert.fingerprint.match(/^system_/)) // starts with system_
);

const pickJobForm = state => state.jobForm;
export const getJobFormSection = createSelector(
  [pickJobForm],
  formState => formState.currentSection
);

export const getJobFormTemplateId = createSelector(
  [pickJobForm],
  formState => formState.selectedTemplate
);

const pickBadges = state => state.badges;
export const getBadges = createSelector(
  [pickBadges],
  badges => (badges === undefined ? {} : badges)
);

export const getBadge = (badgePath) => createSelector(
  [pickBadges],
  badges => (badges === undefined ? undefined : badges[badgePath])
);

const pickUser = state => state.user;
export const getUser = createSelector(
  [pickUser],
  user => user || {}
);

export const getCustomer = createSelector(
  [pickUser],
  user => get(user, 'customer', {})
);

export const getCustomerPayTypes = createSelector(
  [pickUser],
  user => get(user, 'customer.customerPayTypes', [])
);

export const getCompanyAdmin = createSelector(
  [pickUser],
  user => user.companyAdmin
);

const pickCompanySettings = state => state.settings && state.settings.company;
export const getCompanySettings = createSelector(
  [pickCompanySettings],
  settings => (settings === undefined ? {} : settings)
);

const pickTeams = state => state.teams;

export const getActiveTeams = createSelector(
  [pickTeams],
  teams => (teams === undefined ? [] : teams.filter(t => !t.deleted))
);

const pickFillAdvisorData = state => state.fillAdvisorData;
export const getFillAdvisorData = createSelector(
  [pickFillAdvisorData],
  data => (data === undefined ? {} : data)
);

const pickMagic = state => state.magicAlgorithm;

export const getMagicByCategoryAndZip = (category, zip) => createSelector(
  [pickMagic],
  (magicAlgorithmData) => {
    if (!magicAlgorithmData || !magicAlgorithmData.zipIndex) {
      return undefined;
    }
    const userMarket = magicAlgorithmData.zipIndex.map(index => {
      const [start, end, market] = index.split(':');
      return {
        lower: parseInt(start, 10),
        upper: parseInt(end, 10),
        market
      };
    }).filter(index => {
      const zipInt = parseInt(zip, 10);
      const matches = zipInt >= index.lower && zipInt <= index.upper;
      return matches;
    });
    if (userMarket.length > 0) {
      const territory = userMarket[0].market;
      return get(magicAlgorithmData, `attribute.${category}.${territory}`, {});
    }
    return {};
  }
);

const pickWorkers = state => state.workers;
export const getWorkers = createSelector(
  [pickWorkers],
  workers => (workers === undefined ? [] : workers)
);

export const getCanSeeBudgets = createSelector(
  [getCompanySettings],
  settings => settings.canSeeBudgets || false
);

export const getUseStartAndCompletePin = createSelector(
  [getCompanySettings],
  settings => settings.useStartAndCompletePin || false
);

const pickRequestorInfo = state => state.requestor;
export const getRequestor = createSelector(
  [pickRequestorInfo],
  requestor => (requestor === undefined ? {} : requestor)
);

const pickAuth = state => state[storeConstants.AUTH] && state[storeConstants.AUTH].authenticated;
export const getUserAuthentication = createSelector(
  [pickAuth],
  authd => (authd === undefined ? false : authd)
);

const pickJobRequests = state => state.jobRequests;
export const getJobRequests = createSelector(
  [pickJobRequests],
  jobRequests => (jobRequests === undefined ? [] : jobRequests)
);

const pickErrors = state => state.errors;
export const getErrors = createSelector(
  [pickErrors],
  errors => errors || {}
);

// loading //////////////////////////
const pickLoadingState = state => state.loading;

export const getIsLoading = createSelector(
  [pickLoadingState],
  loading => loading || {}
);

const pickLoadingByFingerprint = (state, fingerprint) => pickLoadingState(state)[fingerprint];

export const isLoadingByFingerprint = createCachedSelector(
  [pickLoadingByFingerprint],
  isLoading => (isLoading === undefined ? true : isLoading)
)(
  (state, fingerprint) => fingerprint
);

// //////////////////////////

const pickTheme = state => state[storeConstants.THEME] || {};
export const getTheme = createSelector(
  [pickTheme],
  theme => theme
);

const pickUploadedImages = state => state.imageUpload;
export const getUploadedImages = createSelector(
  [pickUploadedImages],
  images => images || {}
);

const pickCurrentUser = state => state.user;
export const getCurrentUser = createSelector(
  [pickCurrentUser],
  user => user || {}
);
export const getCurrentUserId = createSelector(
  [pickCurrentUser],
  user => user.id
);

const pickPaymentMethods = state => state.paymentMethods;
export const getPaymentMethods = createSelector(
  [pickPaymentMethods],
  paymentMethods => paymentMethods || {}
);

const pickPreferredModal = state => state[storeConstants.PREFERRED_MODAL];
export const getPreferredModalSelectedWonoloers = createSelector(
  [pickPreferredModal],
  ({ isClosedPreferred: _isClosedPreferred, ...preferredWonoloers }) => Object.values(preferredWonoloers) || []
);

export const getPreferredModalIsClosedPreferred = createSelector(
  [pickPreferredModal],
  ({ isClosedPreferred }) => isClosedPreferred
);

const pickAllTemplates = state => state.templates;
export const getAllTemplates = createSelector(
  [pickAllTemplates],
  templates => templates
);

const pickMessagesInboxPage = state => state.messagesInboxPage;
export const getMessagesInboxPage = createSelector(
  [pickMessagesInboxPage],
  messagesInboxPageData => (messagesInboxPageData === undefined ? [] : messagesInboxPageData)
);

const pickMessagesPage = state => state.messagesPage;
export const getMessagesPage = createSelector(
  [pickMessagesPage],
  messagesPageData => (messagesPageData === undefined ? [] : messagesPageData)
);

const pickAllWonoloers = state => state.wonoloers;
export const getAllWonoloers = createSelector(
  [pickAllWonoloers],
  wonoloers => (wonoloers === undefined ? [] : wonoloers)
);

const pickJobFormActiveSection = state => state.jobFormActiveSection;

export const getJobFormActiveSection = createSelector(
  [pickJobFormActiveSection],
  activeSections => activeSections.sectionId
);

const pickJobFormValidSection = state => state.jobFormValidSections;

export const getJobFormValidSection = createCachedSelector(
  pickJobFormValidSection,
  (state, sectionId) => `${sectionId}`,
  (validSections, sectionId) => validSections[sectionId]
)(
  (_state_, sectionId) => sectionId
);

export const getJobFormValid = createSelector(
  [pickJobFormValidSection],
  validSections => Object.keys(validSections).reduce((bool, section) => {
    const isValid = validSections[section];
    return isValid && bool;
  }, true)
);

const pickPagination = state => state[storeConstants.PAGING];
export const getPagination = createSelector(
  [pickPagination],
  cfg => cfg || {}
);

const pickSort = state => state[storeConstants.SORT];
export const getCurrentSort = createSelector(
  [pickSort],
  sort => sort || {}
);

const pickRequirementsTags = state => state[storeConstants.REQUIREMENTS_TAGS];
export const getRequirementsTags = createSelector(
  [pickRequirementsTags],
  tags => tags
);

const pickFeatureUserSettings = state => state[storeConstants.FEATURE_USER_SETTINGS];
export const getFeatureUserSettings = createSelector(
  [pickFeatureUserSettings],
  featureUserSettings => featureUserSettings
);

const pickCustomerPermissions = state => state[storeConstants.CUSTOMER_PERMISSIONS];
export const getCustomerPermissions = createSelector(
  [pickCustomerPermissions],
  customerPermissions => customerPermissions || {}
);

const pickBrowserStorage = state => state[storeConstants.BROWSER_STORAGE];
export const getStorageItem = (fingerprint) => createSelector(
  [pickBrowserStorage],
  (storage) => get(storage, fingerprint, {})
);

export const getStorage = () => createSelector(
  [pickBrowserStorage],
  (storage) => storage
);

/** Job Message Threads
 are messages associated to a single job by the "job_id" column on the messages table
*/
const pickMessageThreadSelections = state => state[storeConstants.MESSAGES_PAGE];
// object of key value pairs where each key is the id of a selected job id
export const getMessageThreadSelections = pickMessageThreadSelections;


const pickOnsiteDeviceVenueSelections = state => state[storeConstants.WANDAS_PAGE];
// object of key value pairs where each key is the id of a selected onsite_device id
export const getWandaVenueModalData = pickOnsiteDeviceVenueSelections;
